import type { Permission } from '@clinic/contracts';
import type { Component } from 'svelte';
import { matchPath } from '../shared/router/router.svelte';

/** A page, loaded on first use: every page is a separate chunk (Master Prompt §64). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PageLoader = () => Promise<{ default: Component<any> }>;

export type Resolved =
  | { kind: 'public'; key: string; load: PageLoader }
  | { kind: 'auth'; key: string; load?: PageLoader; redirect?: true }
  | {
      kind: 'org';
      key: string;
      orgId: string;
      params: Record<string, string>;
      load: PageLoader;
      /** The page's permission: one, or any of several. Absent for the org home. */
      permission?: Permission | readonly Permission[];
    }
  | { kind: 'notFound'; key: string };

const PUBLIC: Record<string, PageLoader> = {
  '/login': () => import('../features/auth/pages/LoginPage.svelte'),
  '/set-password': () => import('../features/auth/pages/SetPasswordPage.svelte'),
};

const AUTH: Record<string, PageLoader> = {
  '/onboarding': () => import('../features/onboarding/pages/CreateClinicPage.svelte'),
  '/orgs': () => import('../features/org/pages/OrgPickerPage.svelte'),
};

interface OrgPage {
  path: string;
  permission?: Permission | readonly Permission[];
  load: PageLoader;
}

/** The same table as the React site's router (apps/web/src/app/router.tsx). */
const ORG: OrgPage[] = [
  { path: '', load: () => import('../features/org/pages/OrgHomePage.svelte') },
  { path: 'members', permission: 'members.read', load: () => import('../features/members/pages/MembersPage.svelte') },
  { path: 'clinics', permission: 'clinics.read', load: () => import('../features/platform/pages/ClinicsPage.svelte') },
  { path: 'clinics/:clinicId', permission: 'clinics.read', load: () => import('../features/platform/pages/ClinicPage.svelte') },
  { path: 'gtins', permission: 'catalog.review', load: () => import('../features/catalog/pages/CatalogPage.svelte') },
  { path: 'default-items', permission: 'catalog.review', load: () => import('../features/catalog/pages/DefaultItemsPage.svelte') },
  { path: 'insights', permission: 'insights.read', load: () => import('../features/insights/pages/InsightsPage.svelte') },
  {
    path: 'insights/clinics/:clinicId',
    permission: 'insights.clinic_read',
    load: () => import('../features/insights/pages/ClinicInsightsPage.svelte'),
  },
  { path: 'audit', permission: 'audit.read', load: () => import('../features/audit/pages/AuditPage.svelte') },
  {
    path: 'calendar',
    permission: 'appointments.read',
    load: () => import('../features/scheduling/pages/CalendarPage.svelte'),
  },
  { path: 'patients', permission: 'patients.read', load: () => import('../features/patients/pages/PatientsPage.svelte') },
  {
    path: 'patients/import',
    permission: 'patients.import',
    load: () => import('../features/data-transfer/pages/PatientImportPage.svelte'),
  },
  {
    path: 'patients/:patientId',
    permission: 'patients.read',
    load: () => import('../features/patients/pages/PatientPage.svelte'),
  },
  { path: 'visits/:visitId', permission: 'visits.read', load: () => import('../features/visits/pages/VisitPage.svelte') },
  {
    path: 'finance',
    permission: ['finance.read', 'balances.read'],
    load: () => import('../features/finance/pages/FinancePage.svelte'),
  },
  {
    path: 'inventory',
    permission: 'inventory.read',
    load: () => import('../features/inventory/pages/InventoryPage.svelte'),
  },
  { path: 'sms', permission: 'sms.send', load: () => import('../features/sms/pages/SmsQueuePage.svelte') },
  {
    path: 'settings',
    permission: ['settings.manage', 'practitioner_settings.manage_own'],
    load: () => import('../features/settings/pages/SettingsPage.svelte'),
  },
];

export function resolve(pathname: string): Resolved {
  const path = pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;
  const pub = PUBLIC[path];
  if (pub) return { kind: 'public', key: path, load: pub };
  if (path === '/') return { kind: 'auth', key: '/', redirect: true };
  const auth = AUTH[path];
  if (auth) return { kind: 'auth', key: path, load: auth };
  for (const page of ORG) {
    const match = matchPath(page.path ? `/o/:orgId/${page.path}` : '/o/:orgId', path);
    if (match) {
      return {
        kind: 'org',
        key: page.path || 'home',
        orgId: match.params.orgId as string,
        params: match.params,
        load: page.load,
        ...(page.permission ? { permission: page.permission } : {}),
      };
    }
  }
  return { kind: 'notFound', key: '*' };
}

/**
 * Loads the code of every page the member may open, one after another while the phone is idle, so
 * a page opens at once instead of waiting for its code on a slow connection (employer report
 * 2026-10-15: 1-2 s with nothing on screen). Each import is cached, so Lazy later resolves at once.
 */
export function preloadOrgPages(can: (permission: Permission) => boolean): () => void {
  const queue = ORG.filter((p) => !p.permission || (typeof p.permission === 'string' ? [p.permission] : p.permission).some(can));
  let stopped = false;
  let handle = 0;
  const hasIdle = 'requestIdleCallback' in window;
  const idle = (fn: () => void) => (hasIdle ? window.requestIdleCallback(fn, { timeout: 2000 }) : window.setTimeout(fn, 200));
  const next = () => {
    const page = queue.shift();
    if (stopped || !page) return;
    page.load().then(
      () => (handle = idle(next)),
      () => (handle = idle(next)),
    );
  };
  handle = idle(next);
  return () => {
    stopped = true;
    if (hasIdle) window.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
  };
}
